import React, {Fragment} from 'react';
import {Page, Text, View, Document, StyleSheet, Image} from '@react-pdf/renderer';
import PlannjaLogo from '../assets/images/plannja_logo.png';
import PiristeelLogo from '../assets/images/Piristeel_logo.png';
import {useTranslation} from "react-i18next";
import TableRowType from "../types/TableRowType";
import NumberFormat from 'react-number-format';
import Row from '../types/Row';
import { formatWeight, formatNumber, detectBestLocale } from '../utils/utils';

const styles = StyleSheet.create({
    body: {
        paddingBottom: 65,
        paddingHorizontal: 0,
    },
    title: {
        fontSize: 20,
        fontFamily: 'Helvetica',
        fontWeight: 'bold',
        textAlign: "center"
    },
    rowsFooter: {
        flexDirection: "row",
        width: "100%",
        borderTop: "1px dotted grey",
        marginBottom: "10px"
    },
    tableContainer: {
        marginHorizontal: "5%",
        marginTop: "5%",
        flexDirection: "row",
        fontFamily: "Helvetica"
    },
    productContainer: {
        marginBottom: "5%",
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        alignSelf: 'flex-start',
        alignContent: 'flex-start',
    },
    productHeaderRow: {
        flexDirection: "row",
        width: '100%',
        height: '20px',
        fontWeight: "bold",
        flexWrap: 'wrap',
        fontFamily: 'Helvetica-Bold'
    },
    productRow: {
        flexDirection: "row",
        width: "100%",
        borderTop: "1px dotted grey",
        flexWrap: 'wrap',
        paddingVertical: 2
    },
    productSummaryRow: {
        flexDirection: "row",
        width: '100%',
        /*height: "30px",*/
        alignContent: "flex-start",
        alignItems: "flex-start",
        borderBottom: "1px dotted grey",
        borderTop: "1px solid grey",
        flexWrap: 'wrap',
        paddingVertical: 5
    },
    headerRow: {
        marginTop: "5%",
        marginHorizontal: "5%",
        flexDirection: "row",
        width: '100%',
        fontWeight: "bold",
        height: '20px',
    },
    footerRow: {
        fontFamily: "Helvetica",
        flexDirection: "row",
        marginHorizontal: "5%",
        height: '20px',
        alignContent: "center",
        alignItems: "center",
        borderTop: "1px solid grey",
        borderBottom: "1px solid grey",
        marginTop: "5px",
        fontWeight: "bold",
    },
    designationHeader: {
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        width: "75%",
        paddingHorizontal: 3
    },
    footerText: {
        width: "25%",
        paddingHorizontal: 3,
        fontFamily: "Helvetica-Bold"
    },
    description: {
        flexDirection: "row",
        width: "50%",
        fontWeight: "normal",
        paddingHorizontal: 3
    },
    netPrice: {
        flexDirection: "row",
        width: "25%",
        paddingHorizontal: 3,
        textAlign: "right",
    },
    footerNetPrice: {
        flexDirection: "row",
        width: "25%",
        paddingHorizontal: 3,
        textAlign: "right",
        fontFamily: "Helvetica-Bold"
    },
    page: {
        paddingTop: 80,
        paddingBottom: 65,
        fontSize: 8,
        flexDirection: "column",
    },
    product: {
        flexDirection: "row",
        width: "35%",
        flexGrow: 1,
        flexShrink: 0,
        paddingHorizontal: 3,
    },
    code: {
        flexDirection: "row",
        width: "15%",

        paddingHorizontal: 3,
    },
    amount: {
        flexDirection: "row",
        width: "10%",
        paddingHorizontal: 3,
        textAlign: "center",
    },
    weight: {
        flexDirection: "row",
        width: "8%",
        paddingHorizontal: 3,
        textAlign: "right",
    },
    totalWeight: {
        flexDirection: "row",
        width: "8%",
        paddingHorizontal: 3,
        textAlign: "right",
    },
    pricePerArticle: {
        flexDirection: "row",
        width: "10%",
        paddingHorizontal: 3,
        textAlign: "right",
    },
    summaryText: {
        width: "82%",
        paddingHorizontal: 3,
        fontFamily: "Helvetica",
    },
    discount: {
        flexDirection: "row",
        width: "12%",
        paddingHorizontal: 3,
        textAlign: "right",
    },
    net_price: {
        flexDirection: "row",
        width: "10%",
        textAlign: "right",
        paddingHorizontal: 3,
    },
    pageHeader: {
        top: "35px",
        left: "5%",
        position: "absolute"
    },
    pageFooter: {
        position: 'absolute',
        fontSize: 10,
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: 'center',
        color: 'black',
    },
});

type SummaryTableProps = {
    summaryTable: TableRowType[]
    countryCode?: string,
    fileName?: string
}

const SummaryTablePDF = (props: SummaryTableProps) => {
    const {t} = useTranslation("trans");
    const {summaryTable, countryCode, fileName} = props;
    let logo: string;
    switch (countryCode) {
        case "se": {
            logo = PlannjaLogo;
            break;
        }
        case "no": {
            logo = PlannjaLogo;
            break;
        }
        case "piristeel": {
            logo = PiristeelLogo;
            break;
        }
        default: {
            logo = PlannjaLogo;
            break;
        }
    }

    return (<Document language='swedish'>
        <Page size="A4" style={styles.page} wrap>
            <View style={styles.pageHeader}>
                <Image src={logo} style={{maxHeight: "50px", maxWidth: "150px", width: "auto", height: "auto"}}/>
            </View>
            <View style={styles.body}>
                {/* 🟢 Huvudrubriken: "Sammanfattning av taksäkerhetsprodukter"  */}
                <Text style={styles.title}>{t("pdf.summaryTableTitle")}</Text>
                
                {/* 🟢 Tabellerna */}
                <TableRow summaryTable={summaryTable}/>
                
                {/* 🟢 Total summering av alla tabeller  */}
                <TableFooter summaryTable={summaryTable}/>
            </View>
            <Text style={styles.pageFooter} fixed>{fileName}</Text>
        </Page>
    </Document>)
};

const ProductRows = (props: { rows: Row[] }) => {
    const {t} = useTranslation("trans");
    const {rows} = props;
    const locale = detectBestLocale();
    
    if (!rows) return null;
    
    const totalWeight = rows.map(x => x.weight).reduce((x, xp) => x + xp, 0.0);
    const totalPrice = rows.map(x => x.netPrice).reduce((x, xp) => x + xp, 0.0);
    
    const headerRow = (
        <View style={styles.productHeaderRow}>
            <Text style={styles.code}>{t("table.artNr")}</Text>
            <Text style={styles.product}>{t("table.term")}</Text>
            <Text style={styles.amount}>{t("table.qty")}</Text>
            <Text style={styles.discount}>{t("table.discount")}</Text>
            <Text style={styles.pricePerArticle}>{t("table.price/art")}</Text>
            <Text style={styles.weight}>{t("table.weight")}</Text>
            <Text style={styles.net_price}>{t("table.net_price")}</Text>
        </View>
    );

    const content = rows.map((row, i) => (
        <View style={styles.productRow} key={i.toString()}>
            <Text style={styles.code}>{row.art}</Text>
            <Text style={styles.product}>{row.term}</Text>
            <Text style={styles.amount}>{row.amount}</Text>
            <Text style={styles.discount}>{row.discount}</Text>
            <Text style={styles.pricePerArticle}> { formatNumber(row.fullPrice, locale, 0) }</Text>
            <Text style={styles.weight}> { formatWeight(row.weight, locale, false, 1) }</Text>
            <Text style={styles.net_price}>{ formatNumber(row.netPrice, locale, 0) }</Text>
        </View>
    ));

    const footerRow = () => (
        <View style={styles.rowsFooter}>
            <Text style={styles.summaryText}>{t("table.summary")}</Text>
            <Text style={styles.totalWeight}> { formatWeight(totalWeight, locale, true, 1) }</Text>
            <Text style={styles.net_price}>{ formatNumber(totalPrice, locale, 0) }</Text>
        </View>
    );

    let ret = [];
    ret.push(headerRow)
    content.map(x => ret.push(x));
    ret.push(footerRow())
    return <Fragment>{ret}</Fragment>;
}

const TableRow = (props: SummaryTableProps) => {
    const {t} = useTranslation("trans");
    const {summaryTable} = props;
    const locale = detectBestLocale();
    if (summaryTable == null) return null;

    const rows = summaryTable.map((item, i) => {
        const title = item.name ? `${item.name}, ${item.description}` : item.description;
        return (
            <View key={i.toString()} break={i > 1} style={styles.tableContainer}>
                <View style={styles.productContainer}>
                    {/* 🟢 HEADER - Rubriker */}
                    <View style={styles.productHeaderRow}>
                        <Text style={styles.designationHeader}>{t("table.designation").toUpperCase()}</Text>
                        <Text style={styles.netPrice}>{t("table.net_price").toUpperCase()}</Text>
                    </View>

                    {/* 🟢 SUMMERING - Visar summerad info överst i tabellen */}
                    <View style={styles.productSummaryRow}>
                        <Text style={styles.designationHeader}>{title}</Text>
                        <Text style={styles.netPrice}>{formatNumber(item.netPrice, locale, 0)}</Text>
                    </View>
                    
                    {/* 🟢 PRODUKTRADER - Artiklarna under summeringen */}
                    <ProductRows rows={item.row}/>
                </View>
            </View>
        )
    });
    return <Fragment>{rows}</Fragment>;
};

const TableFooter = (props: SummaryTableProps) => {
    const {summaryTable} = props;
    const {t} = useTranslation("trans");
    const locale = detectBestLocale();
    const footerTotalPrice = summaryTable.reduce((a, b) => a + (b.netPrice ? b.netPrice : 0), 0);

    /* 🟢 Total summering av alla tabeller (dvs är det 1 gångbrygga och 1 informationsskylt kommer först 2 tabeller, därefter denna): Texten "Total sammanfattning" + "Beräknat pris exkl. moms. Ev. frakt..." + totalsumma */
    return (
        <View style={styles.footerRow} key={summaryTable.length.toString()}>
            <Text style={styles.footerText}>{t("table.totalSummary")}</Text>
            <Text style={styles.description}>{t("table.description")}</Text>
            <Text style={styles.footerNetPrice}>{ formatNumber(footerTotalPrice, locale, 0)}</Text>
        </View>
    );
}

export {SummaryTablePDF};
